import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Alert, Chip, Box,
  MenuItem, Select, InputLabel, FormControl,
} from '@mui/material';
import { Add, Edit, Delete, Circle } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

interface Agent {
  id: number;
  tenant_id: number;
  agent_id: string;
  agent_type: string;
  name: string;
  station_id: number | null;
  is_active: boolean;
  is_online: boolean;
  last_heartbeat: string | null;
  last_connected: string | null;
  last_disconnected: string | null;
  version: string | null;
  ip_address: string | null;
  notes: string | null;
}

const AGENT_TYPES = ['dispatch', 'station', 'radio'];
const EMPTY_FORM = { agent_id: '', agent_type: 'station', name: '', station_id: '', notes: '' };

export default function AgentsPage() {
  const { tenantId } = useAuth();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/tenants/${tenantId}/agents`);
      setAgents(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setDialogOpen(true); };

  const openEdit = (a: Agent) => {
    setEditingId(a.id);
    setForm({
      agent_id: a.agent_id, agent_type: a.agent_type, name: a.name,
      station_id: a.station_id?.toString() || '', notes: a.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!tenantId) return;
    const payload: Record<string, unknown> = {
      name: form.name, notes: form.notes || null,
      station_id: form.station_id ? parseInt(form.station_id) : null,
    };
    try {
      if (editingId) {
        await api.put(`/api/tenants/${tenantId}/agents/${editingId}`, payload);
      } else {
        await api.post(`/api/tenants/${tenantId}/agents`, {
          ...payload, agent_id: form.agent_id, agent_type: form.agent_type,
        });
      }
      setDialogOpen(false);
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (id: number) => {
    if (!tenantId || !window.confirm('Delete this agent?')) return;
    try {
      await api.delete(`/api/tenants/${tenantId}/agents/${id}`);
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Delete failed');
    }
  };

  const updateField = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  const formatTime = (iso: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString();
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">Agents</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Register Agent</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Agent ID</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>IP Address</TableCell>
              <TableCell>Version</TableCell>
              <TableCell>Last Heartbeat</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} align="center">Loading...</TableCell></TableRow>
            ) : agents.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center">No agents registered</TableCell></TableRow>
            ) : agents.map(a => (
              <TableRow key={a.id}>
                <TableCell>
                  <Circle sx={{ fontSize: 12, color: a.is_online ? 'success.main' : 'error.main', mr: 1 }} />
                  {a.is_online ? 'Online' : 'Offline'}
                </TableCell>
                <TableCell>{a.name}</TableCell>
                <TableCell><code>{a.agent_id}</code></TableCell>
                <TableCell>
                  <Chip label={a.agent_type} size="small" color={
                    a.agent_type === 'dispatch' ? 'primary' : a.agent_type === 'station' ? 'success' : 'warning'
                  } />
                </TableCell>
                <TableCell>{a.ip_address || '—'}</TableCell>
                <TableCell>{a.version || '—'}</TableCell>
                <TableCell>{formatTime(a.last_heartbeat)}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => openEdit(a)}><Edit fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(a.id)}><Delete fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Agent' : 'Register Agent'}</DialogTitle>
        <DialogContent>
          {!editingId && (
            <>
              <TextField label="Agent ID" fullWidth margin="dense" value={form.agent_id} onChange={updateField('agent_id')} required
                helperText="Unique identifier for this agent instance" />
              <FormControl fullWidth margin="dense">
                <InputLabel>Agent Type</InputLabel>
                <Select value={form.agent_type} label="Agent Type"
                  onChange={(e) => setForm(prev => ({ ...prev, agent_type: e.target.value }))}>
                  {AGENT_TYPES.map(t => <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </>
          )}
          <TextField label="Display Name" fullWidth margin="dense" value={form.name} onChange={updateField('name')} required />
          <TextField label="Station ID" fullWidth margin="dense" value={form.station_id} onChange={updateField('station_id')} type="number"
            helperText="Assign to a station (station/radio agents)" />
          <TextField label="Notes" fullWidth margin="dense" value={form.notes} onChange={updateField('notes')} multiline rows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name || (!editingId && !form.agent_id)}>Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
